import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const fetchContacts = createAsyncThunk('contacts/fetch', async (params) => {
  const response = await api.get('/contacts', { params });
  return response.data;
});

export const fetchContactsForDashboard = createAsyncThunk('contacts/fetchForDashboard', async () => {
  // Use optimized endpoint that returns only counts, not all contact data
  const response = await api.get('/contacts/dashboard-stats');
  return response.data;
});

export const createContact = createAsyncThunk('contacts/create', async (data) => {
  const response = await api.post('/contacts', data);
  return response.data;
});

export const bulkCreateContacts = createAsyncThunk('contacts/bulkCreate', async (data) => {
  const response = await api.post('/contacts/bulk', data);
  return response.data;
});

export const updateContact = createAsyncThunk('contacts/update', async ({ id, data }) => {
  const response = await api.put(`/contacts/${id}`, data);
  return response.data;
});

export const deleteContact = createAsyncThunk('contacts/delete', async (id) => {
  await api.delete(`/contacts/${id}`);
  return id;
});

const contactSlice = createSlice({
  name: 'contacts',
  initialState: {
    items: [],
    total: 0,
    currentPage: 1,
    itemsPerPage: 25, // Default items per page
    totalPages: 0,
    loading: false,
    error: null,
    // Dashboard-specific data
    dashboardContacts: [],
    dashboardLoading: false,
    institutionTypeBreakdown: {} // Institution type statistics for dashboard
  },
  reducers: {
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1; // Reset to first page when changing items per page
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.contacts;
        state.total = action.payload.total;
        state.totalPages = action.payload.pages || Math.ceil(action.payload.total / state.itemsPerPage);
        // Update current page from response if provided
        if (action.payload.page) {
          state.currentPage = action.payload.page;
        }
      })
      .addCase(fetchContactsForDashboard.pending, (state) => {
        state.dashboardLoading = true;
      })
      .addCase(fetchContactsForDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        // New format: { success: true, data: { totalContacts, institutionTypeBreakdown } }
        if (action.payload.success && action.payload.data) {
          state.total = action.payload.data.totalContacts;
          state.institutionTypeBreakdown = action.payload.data.institutionTypeBreakdown || {};
          state.dashboardContacts = []; // No longer fetching actual contacts, just counts
        }
      })
      .addCase(createContact.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
        state.totalPages = Math.ceil(state.total / state.itemsPerPage);
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        const index = state.items.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.items = state.items.filter(c => c.id !== action.payload);
        state.total -= 1;
        state.totalPages = Math.ceil(state.total / state.itemsPerPage);
        // If current page is empty and not the first page, go to previous page
        if (state.items.length === 0 && state.currentPage > 1) {
          state.currentPage -= 1;
        }
      });
  }
});

export const { setPage, setItemsPerPage } = contactSlice.actions;

export default contactSlice.reducer;
